<template>
  <div class="tournament-table">
    <div class="t-table-header-sub">
      <div class="i-am-just-here-for-cheating-on-flexbox"></div>
      <c-search-input
        @on-change="onSearchTerm"
        :placeholder="$t('table.search.input_placeholder')"
        data-qe-id="search-input"
      />
    </div>
    <table class="t-table">
      <thead>
        <tr>
          <td width="15%">{{ $t('table.columns.rank') }}</td>
          <td width="25%">{{ $t('table.columns.playerName') }}</td>
          <td width="25%">{{ $t('table.columns.score') }}</td>
          <td width="20%">{{ $t('table.columns.gamesFinished') }}</td>
          <c-table-head-column
            width="15%"
            :is-selected="true"
            :sort-filter="SORT_FILTER.TotalWin"
            :sort-order="currentSorting"
            @toggle-sorting="onToggleSorting"
            qe-id="sort-by-prize"
          >
            {{ $t('table.columns.totalWin') }}
            <template v-slot:icon>
              <t-tooltip v-if="!showHistoryMode" class="tooltip" :text="$t('table.info.totalWin')">
                <i class="icf-info" />
              </t-tooltip>
            </template>
          </c-table-head-column>
        </tr>
      </thead>
      <tbody data-qe-id="table-data">
        <template v-if="tournamentRankingsRange.length > 0">
          <tr
            v-for="entry in tournamentRankingsRange"
            :key="entry.playerName"
            :class="isMyUser(entry) ? 'highlighted' : ''"
            :data-qe-id="isMyUser(entry) ? 'tr-data-highlighted' : 'tr-data'"
          >
            <td>{{ entry.tournamentRank }}</td>
            <td>{{ entry.playerName }}</td>
            <td>
              <span v-if="isMyUser(entry)" data-qe-id="current-score">{{
                showHistoryMode || hasPlayedGames ? entry.tournamentScore : '-'
              }}</span>
              <span v-else>{{ entry.tournamentScore }}</span>
              <span
                v-if="
                  !showHistoryMode &&
                  entry.playerId === store.getters.userId &&
                  store.state.ranking.tournament.prevBestScore
                "
                class="inline-info"
                data-qe-id="previous-best-score"
              >
                ({{
                  $t('tViews.joined.text_tournamentInfo_bestScorePartial', {
                    prevBestScore: store.state.ranking.tournament.prevBestScore,
                  })
                }}
                <!-- ({{ $store.state.ranking.tournament.prevBestScore }} -->
                <t-tooltip :text="$t('table.info.previousPoints')"><i class="icf-info info-icon" /></t-tooltip>)
              </span>
            </td>
            <td>{{ entry.playedGames }} / {{ maxGamesPerTournament }}</td>
            <td>{{ entry.totalWin }}</td>
          </tr>

          <!-- "show more entries" button/row -->
          <template v-if="maxEntries > entriesToRender">
            <tr>
              <td class="dots" :colspan="COLSPAN_NUM">• • •</td>
            </tr>
            <tr>
              <td class="show-more-row" :colspan="COLSPAN_NUM" @click="onShowMore" data-qe-id="show-more-link">
                {{ $t('table.more.button') }} <i class="icf-arrowBottom"></i>
              </td>
            </tr>
          </template>

          <tr
            v-if="!isSearchActive && myRanking && !isMyUserInViewPort"
            class="highlighted"
            data-qe-id="tr-data-highlighted--outside"
          >
            <td>{{ myRanking.tournamentRank }}</td>
            <td>{{ myRanking.playerName }}</td>
            <td>{{ myRanking.tournamentScore }}</td>
            <td>{{ myRanking.playedGames }} / {{ maxGamesPerTournament }}</td>
            <td>{{ myRanking.totalWin }}</td>
          </tr>
          <tr v-if="isSearchActive && tournamentRankingsRange.length === 0" class="search-not-found">
            <td :colspan="COLSPAN_NUM">
              {{ $t('table.search.results_not_found') }}
            </td>
          </tr>
        </template>
        <template v-else>
          <tr class="no-results" data-qe-id="no-results-available-row">
            <td :colspan="COLSPAN_NUM">{{ $t('table.more.no_results') }}</td>
          </tr>
        </template>
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from '@/store'
import { marked } from 'marked'
import { TournamentRanking } from '@/model/ranking/TournamentRankings'
import { State } from '@/store/state'
import { Sorting, SortFilter, SortOrder } from '@/model/ranking/Sorting'
import CTableHeadColumn from '@/components/ranking/CTableHeadColumn.vue'
import CSearchInput from '@/components/CSearchInput.vue'
import { jslog } from '@gd/jslog'
import { track } from '@/lib/track'

const props = withDefaults(defineProps<{ tId: string; showHistoryMode?: boolean }>(), { showHistoryMode: false })
const route = useRoute()
const store = useStore()

const COLSPAN_NUM = 5
const ENTRIES_PER_BATCH = 50
const SORT_FILTER = SortFilter

const entriesToRender = ref(ENTRIES_PER_BATCH)
const searchTerm = ref('')

function loadData(tId: string) {
  store.dispatch('ranking/tournament/fetch', tId).catch((err) => {
    jslog.send({ name: 'fatal', data: { message: `could not load tournament table for: ${tId}` } })
  })
}

watch(
  () => props.tId,
  (to) => loadData(to)
)

watch(
  () => store.state.tournament.joined,
  () => loadData(props.tId)
)

function md(text: string): string {
  return marked(text)
}

/**
 * Range of TournamentRankings rendered on screen
 */
const tournamentRankingsRange = computed((): Array<TournamentRanking> => {
  if (isSearchActive.value) {
    return store.getters['ranking/tournament/usersByName'](searchTerm.value, 0, entriesToRender.value)
  }
  return store.getters['ranking/tournament/rankings'](0, entriesToRender.value)
})

/**
 * Sorting stored in ranking/tournament
 */
const currentSorting = computed((): SortOrder => {
  return store.state.ranking.tournament.sorting
})

/**
 * My TournamentRanking dataset filtered from ranking/tournament store
 */
const myRanking = computed((): TournamentRanking => {
  return store.getters['ranking/tournament/rankings']().filter(
    (ranking: TournamentRanking) => ranking.playerId === store.getters.userId
  )[0]
})

/**
 * Displayed in column 'Number of played games'
 */
const maxGamesPerTournament = computed((): number => {
  return (store.state as State).tournament.metaData.numberOfGames
})

/**
 * Amount of all TournamentRankings in store
 */
const maxEntries = computed(() => {
  if (isSearchActive.value) {
    return store.getters['ranking/tournament/usersByName'](searchTerm.value).length
  }
  return store.getters['ranking/tournament/rankings']().length
})

/**
 * Consider search as active when minimum amount of characters is reached
 */
const isSearchActive = computed(() => {
  return searchTerm.value.length > 0
})

function isMyUser(ranking: TournamentRanking): boolean {
  // userId in userData call does not contain leading "u", table data does have a "u"?
  return store.getters.userId === ranking.playerId
}

const hasPlayedGames = computed(() => {
  return (store.state as State).tournament.games.length > 0
})

const isMyUserInViewPort = computed((): boolean => {
  const userId = store.getters.userId
  const filteredUsers = tournamentRankingsRange.value.filter((ranking) => ranking.playerId === userId)
  return filteredUsers.length > 0
})

/**
 * Event handling
 */
function onShowMore() {
  if (entriesToRender.value + ENTRIES_PER_BATCH <= maxEntries.value) {
    entriesToRender.value += ENTRIES_PER_BATCH
    track({
      path: route.fullPath,
      id: 'tournamentTable',
      elem: 'load-more',
    })
  } else {
    entriesToRender.value = maxEntries.value
  }
}

function onToggleSorting(sorting: Sorting) {
  store.commit('ranking/tournament/sorting', sorting.order)
  track({
    path: route.fullPath,
    id: 'tournamentTable',
    elem: 'toggle-sorting',
    data: sorting.order,
  })
}

function onSearchTerm(term: string) {
  searchTerm.value = term
}
loadData(props.tId)
</script>

<style lang="less" scoped>
.inline-info {
  i {
    top: -1px;
    position: relative;
  }
}
</style>
